import {Button, DialogActions, DialogContent, DialogContentText, useMediaQuery} from '@material-ui/core';
import 'Agent/Accounts/components/PolicyInformationReport.sass';
import {deletePolicyInformationReport, getPolicyInformationReports} from 'Agent/Accounts/ducks/account';
import {AccountReportRequest} from 'Agent/Accounts/models/AccountReport';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {fromResult, toEither, toPage} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import Modal from 'Shared/components/Modal';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC, toFormattedDateTimeAsEST} from 'Shared/utilities/DateFormatter';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {NoOp} from 'Shared/utilities/IOUtilities';

type PolicyInformationReportProps = {
  selectedAgency: AgencyFilters;
};

export const getRequestParams = (policyInformationReport: AccountReportRequest, encryptedIdentifier: string) =>
  Boolean(policyInformationReport.eisFlag)
    ? addFilterParameters({encryptedIdentifier}).replace('?', '&')
    : addFilterParameters({encryptedIdentifier});

export const getPolicyInformationReportName = (policyInformationReport: AccountReportRequest) =>
  `PIR-${policyInformationReport.accountNumber}-${toFormattedDateAsUTC(policyInformationReport.requestDate)}${
    Boolean(policyInformationReport.eisFlag) ? FileExtension.XLSX : FileExtension.PDF
  }`;

const PolicyInformationReport = (props: PolicyInformationReportProps) => {
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const match = useRouteMatch();
  useBreadcrumbs('Policy Information Reports', match.url);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {policyInformationReports} = useSelector((state: ReducerState) => state.account);
  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentPIR, setCurrentPIR] = useState<AccountReportRequest | undefined>();

  const getGeneratedPolicyReportActions = (policyInformationReport: AccountReportRequest) => (
    <>
      <Button
        id={`download-pir-btn-${policyInformationReport.reportId}`}
        onClick={() =>
          downloadFile(
            'api/employer' +
              policyInformationReport.link?.href +
              getRequestParams(policyInformationReport, selectedAgency.encryptedIdentifier),
            getPolicyInformationReportName(policyInformationReport)
          )
        }
        color="primary"
        className="policy-information-report__download-button"
      >
        Download
      </Button>
      <Button
        id={`delete-pir-btn-${policyInformationReport.reportId}`}
        className="policy-information-report__delete-button"
        onClick={() => {
          setCurrentPIR(policyInformationReport);
          setConfirmDelete(true);
        }}
      >
        Delete
      </Button>
    </>
  );

  const getGeneratingPolicyReportActions = (report: AccountReportRequest) => (
    <div>
      <span>Generating...</span>
      <Button
        id={`delete-pir-btn-${report.reportId}`}
        className="policy-information-report__delete-button"
        onClick={() => {
          setCurrentPIR(report);
          setConfirmDelete(true);
        }}
      >
        Delete
      </Button>
    </div>
  );

  const getPolicyInformationActions = (report: AccountReportRequest) =>
    report.link?.href ? getGeneratedPolicyReportActions(report) : getGeneratingPolicyReportActions(report);

  const handleDeletion = () => {
    setConfirmDelete(false);
    pipe(
      userClaims,
      toEither,
      E.map(userClaim => {
        dispatch(
          deletePolicyInformationReport(
            selectedAgency.encryptedIdentifier,
            userClaim.id,
            currentPIR!.reportId!,
            currentPIR!.eisFlag
          )
        );
      })
    );
  };

  useEffect(() => {
    pipe(
      userClaims,
      toEither,
      E.map(userClaim => {
        dispatch(getPolicyInformationReports(selectedAgency.encryptedIdentifier, userClaim.id));
      })
    );
  }, [userClaims]);

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'PolicyInformationReports',
    relativeFileLocation: pipe(
      userClaims,
      toEither,
      E.map(uc => {
        return `/api/employer/v1/accounts/reports/${uc.id}/requests${addFilterParameters({
          encryptedIdentifier: selectedAgency.encryptedIdentifier,
          exportToCsv: true,
        })}`;
      }),
      E.fold(
        () => '',
        url => url
      )
    ),
    children: 'Export To CSV',
  };

  return renderAsyncView(policyInformationReports, result => (
    <>
      <PagedView
        pagedViewKey="policyInformationReports"
        loadMore={NoOp}
        data={fromResult(toPage(result))}
        actions={
          <>
            <Button
              id={`refresh-pir-btn`}
              className="policy-information-report__refresh-button"
              variant="contained"
              color="primary"
              onClick={() =>
                pipe(
                  userClaims,
                  toEither,
                  E.map(userClaim => {
                    dispatch(getPolicyInformationReports(selectedAgency.encryptedIdentifier, userClaim.id));
                  })
                )
              }
            >
              Refresh Reports
            </Button>
            {isMobile ? (
              <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
            ) : (
              <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
            )}
          </>
        }
      >
        {columnProps => (
          <>
            <Column {...columnProps} title="Submission Date" default>
              {policyInformationReport => toFormattedDateTimeAsEST(policyInformationReport.requestDate)}
            </Column>
            <Column {...columnProps} title="Account Number" default ddprivacy="mask">
              {policyInformationReport => policyInformationReport.accountNumber}
            </Column>
            <Column {...columnProps} isActions title="" default>
              {getPolicyInformationActions}
            </Column>
          </>
        )}
      </PagedView>
      <Modal title="Delete Policy Information Report?" open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" className='dialog-content-text'>
            Do you want to delete this report? Please note that this action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeletion} color="primary" variant="contained">
            Confirm
          </Button>
          <Button variant="contained" onClick={() => setConfirmDelete(false)} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Modal>
    </>
  ));
};

export default PolicyInformationReport;
